import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import notificationHub from '../../services/notificationHub';
import repositoryContainer from '../../services/RepositoryContainer';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const readPayload = (data, camelCaseKey, pascalCaseKey) => (
  data?.[camelCaseKey] ?? data?.[pascalCaseKey]
);

const createNotificationId = () => (
  globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
);

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [assignedTaskCount, setAssignedTaskCount] = useState(0);
  const [workflowRefreshVersion, setWorkflowRefreshVersion] = useState(0);
  const [recordRefreshVersion, setRecordRefreshVersion] = useState(0);
  const [lastWorkflowEvent, setLastWorkflowEvent] = useState(null);
  const [newBookingCount, setNewBookingCount] = useState(0);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [newRequestOrderCount, setNewRequestOrderCount] = useState(0);

  const companyId = user?.company?.id || null;
  const accountId = user?.id || user?.userId || null;
  const isCompanyMember = user?.roles?.some((role) => (
    ['admin', 'staff'].includes(String(role).toLowerCase())
  ));

  const addNotification = useCallback((notification) => {
    setNotifications((current) => [{
      id: createNotificationId(),
      read: false,
      timestamp: new Date(),
      ...notification,
    }, ...current]);
    setUnreadCount((current) => current + 1);
  }, []);

  const clearNewBookingCount = useCallback(() => setNewBookingCount(0), []);
  const clearNewOrderCount = useCallback(() => setNewOrderCount(0), []);
  const clearNewRequestOrderCount = useCallback(() => setNewRequestOrderCount(0), []);

  const refreshAssignedTaskCount = useCallback(async () => {
    if (!companyId || !isCompanyMember) {
      setAssignedTaskCount(0);
      return [];
    }

    try {
      const raw = await repositoryContainer.workflowInstance.listAssignedToMe();
      const tasks = raw?.response ?? raw;
      const normalizedTasks = Array.isArray(tasks) ? tasks : [];
      setAssignedTaskCount(normalizedTasks.length);
      return normalizedTasks;
    } catch (error) {
      console.error('Failed to refresh assigned workflow task count:', error);
      return null;
    }
  }, [companyId, isCompanyMember]);

  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
    setLastWorkflowEvent(null);
    setWorkflowRefreshVersion(0);
    setRecordRefreshVersion(0);
    setNewBookingCount(0);
    setNewOrderCount(0);
    setNewRequestOrderCount(0);
  }, [accountId]);

  useEffect(() => {
    refreshAssignedTaskCount();
  }, [refreshAssignedTaskCount]);

  useEffect(() => {
    if (!companyId || !isCompanyMember) return undefined;

    const handleNewBooking = (data) => {
      setRecordRefreshVersion((current) => current + 1);
      setNewBookingCount((current) => current + 1);
      addNotification({
        type: 'booking',
        message: `New booking from ${readPayload(data, 'clientName', 'ClientName')}`,
        detail: `${readPayload(data, 'serviceName', 'ServiceName')} — ${readPayload(data, 'date', 'Date')} at ${readPayload(data, 'time', 'Time')}`,
        referenceCode: readPayload(data, 'referenceCode', 'ReferenceCode'),
      });
    };

    const handleNewOrder = (data) => {
      setRecordRefreshVersion((current) => current + 1);
      setNewOrderCount((current) => current + 1);
      addNotification({
        type: 'order',
        message: `New order from ${readPayload(data, 'clientName', 'ClientName')}`,
        detail: `${readPayload(data, 'itemCount', 'ItemCount')} item(s) — GHS ${readPayload(data, 'totalAmount', 'TotalAmount')}`,
        referenceCode: readPayload(data, 'referenceCode', 'ReferenceCode'),
      });
    };

    const handlePaymentConfirmed = (data) => {
      setWorkflowRefreshVersion((current) => current + 1);
      setRecordRefreshVersion((current) => current + 1);
      refreshAssignedTaskCount();
      const sourceReferenceCode = readPayload(data, 'sourceReferenceCode', 'SourceReferenceCode')
        || readPayload(data, 'referenceCode', 'ReferenceCode');
      addNotification({
        type: 'payment',
        message: `Payment confirmed — ${sourceReferenceCode}`,
        detail: `${readPayload(data, 'clientName', 'ClientName') || 'Customer'} · GHS ${readPayload(data, 'amount', 'Amount') || ''}`,
        referenceCode: sourceReferenceCode,
      });
    };

    const handleNewBookingRequest = (data) => {
      setWorkflowRefreshVersion((current) => current + 1);
      setRecordRefreshVersion((current) => current + 1);
      setNewRequestOrderCount((current) => current + 1);
      addNotification({
        type: 'request',
        message: `New request from ${readPayload(data, 'clientName', 'ClientName') || 'a customer'}`,
        detail: `${readPayload(data, 'itemCount', 'ItemCount') || 0} item(s)`,
        referenceCode: readPayload(data, 'referenceCode', 'ReferenceCode'),
      });
    };

    const handleBookingRequestStatusChanged = (data) => {
      setWorkflowRefreshVersion((current) => current + 1);
      setRecordRefreshVersion((current) => current + 1);
      refreshAssignedTaskCount();
      addNotification({
        type: 'request',
        message: `Request updated: ${readPayload(data, 'referenceCode', 'ReferenceCode')}`,
        detail: `Status: ${readPayload(data, 'status', 'Status') || 'updated'}`,
        referenceCode: readPayload(data, 'referenceCode', 'ReferenceCode'),
      });
    };

    const handleCustomerChatMessage = (data) => {
      setRecordRefreshVersion((current) => current + 1);
      const referenceCode = readPayload(data, 'referenceCode', 'ReferenceCode');
      addNotification({
        type: 'chat',
        message: referenceCode ? `New customer message — ${referenceCode}` : 'New customer message',
        detail: readPayload(data, 'senderDisplayName', 'SenderDisplayName') || 'Open the record to continue the conversation.',
        referenceCode,
      });
    };

    const handleBookingPriceFinalized = (data) => {
      setRecordRefreshVersion((current) => current + 1);
      const referenceCode = readPayload(data, 'referenceCode', 'ReferenceCode');
      addNotification({
        type: 'booking',
        message: referenceCode ? `Booking price finalized — ${referenceCode}` : 'Booking price finalized',
        detail: 'The booking total and balance have been updated.',
        referenceCode,
      });
    };

    const handleBookingRequestPriceFinalized = (data) => {
      setRecordRefreshVersion((current) => current + 1);
      setWorkflowRefreshVersion((current) => current + 1);
      setLastWorkflowEvent({ eventName: 'BookingRequestPriceFinalized', data, sequence: Date.now() + Math.random() });
      const referenceCode = readPayload(data, 'referenceCode', 'ReferenceCode');
      addNotification({
        type: 'request',
        message: referenceCode ? `Request price finalized — ${referenceCode}` : 'Request price finalized',
        detail: 'The request total and balance have been updated.',
        referenceCode,
      });
    };

    const workflowMessages = {
      BookingRequestWorkflowStarted: (data) => ({
        message: `Workflow started — ${readPayload(data, 'referenceCode', 'ReferenceCode')}`,
        detail: `${readPayload(data, 'workflowName', 'WorkflowName')} · ${readPayload(data, 'currentStageName', 'CurrentStageName')}`,
      }),
      WorkflowComment: (data) => ({
        message: `Workflow comment — ${readPayload(data, 'referenceCode', 'ReferenceCode')}`,
        detail: `${readPayload(data, 'stageName', 'StageName')} · ${readPayload(data, 'commentedBy', 'CommentedBy')}`,
      }),
      WorkflowStageAdvanced: (data) => ({
        message: `Request advanced — ${readPayload(data, 'referenceCode', 'ReferenceCode')}`,
        detail: `Next stage: ${readPayload(data, 'nextStageName', 'NextStageName')}`,
      }),
      BookingRequestRejected: (data) => ({
        message: `Request rejected — ${readPayload(data, 'referenceCode', 'ReferenceCode')}`,
        detail: `${readPayload(data, 'stageName', 'StageName')} · ${readPayload(data, 'rejectedBy', 'RejectedBy')}`,
      }),
      BookingRequestFullyApproved: (data) => ({
        message: `Request fully approved — ${readPayload(data, 'referenceCode', 'ReferenceCode')}`,
        detail: `${readPayload(data, 'stageName', 'StageName')} · ${readPayload(data, 'approvedBy', 'ApprovedBy')}`,
      }),
    };

    const workflowHandlers = Object.entries(workflowMessages).map(([eventName, getMessage]) => {
      const handler = (data) => {
        setLastWorkflowEvent({ eventName, data, sequence: Date.now() + Math.random() });
        setWorkflowRefreshVersion((current) => current + 1);
        refreshAssignedTaskCount();
        addNotification({
          type: 'workflow',
          referenceCode: readPayload(data, 'referenceCode', 'ReferenceCode'),
          ...getMessage(data),
        });
      };
      notificationHub.on(eventName, handler);
      return [eventName, handler];
    });

    const handleConnectionRestored = () => {
      setWorkflowRefreshVersion((current) => current + 1);
      setRecordRefreshVersion((current) => current + 1);
      refreshAssignedTaskCount();
    };

    notificationHub.on('NewBooking', handleNewBooking);
    notificationHub.on('NewOrder', handleNewOrder);
    notificationHub.on('NewBookingRequest', handleNewBookingRequest);
    notificationHub.on('BookingRequestStatusChanged', handleBookingRequestStatusChanged);
    notificationHub.on('PaymentConfirmed', handlePaymentConfirmed);
    notificationHub.on('CustomerChatMessage', handleCustomerChatMessage);
    notificationHub.on('BookingPriceFinalized', handleBookingPriceFinalized);
    notificationHub.on('BookingRequestPriceFinalized', handleBookingRequestPriceFinalized);
    notificationHub.on('ConnectionRestored', handleConnectionRestored);

    notificationHub.connect(companyId).catch((error) => {
      console.error('Failed to connect notification hub:', error);
    });

    return () => {
      notificationHub.off('NewBooking', handleNewBooking);
      notificationHub.off('NewOrder', handleNewOrder);
      notificationHub.off('NewBookingRequest', handleNewBookingRequest);
      notificationHub.off('BookingRequestStatusChanged', handleBookingRequestStatusChanged);
      notificationHub.off('PaymentConfirmed', handlePaymentConfirmed);
      notificationHub.off('CustomerChatMessage', handleCustomerChatMessage);
      notificationHub.off('BookingPriceFinalized', handleBookingPriceFinalized);
      notificationHub.off('BookingRequestPriceFinalized', handleBookingRequestPriceFinalized);
      notificationHub.off('ConnectionRestored', handleConnectionRestored);
      workflowHandlers.forEach(([eventName, handler]) => notificationHub.off(eventName, handler));
      notificationHub.disconnect(companyId);
    };
  }, [addNotification, companyId, isCompanyMember, refreshAssignedTaskCount]);

  const markAllRead = () => {
    setNotifications((current) => current.map((notification) => ({ ...notification, read: true })));
    setUnreadCount(0);
  };

  const markRead = (id) => {
    const wasUnread = notifications.some((notification) => notification.id === id && !notification.read);
    setNotifications((current) => current.map((notification) => (
      notification.id === id ? { ...notification, read: true } : notification
    )));
    if (wasUnread) setUnreadCount((current) => Math.max(0, current - 1));
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      assignedTaskCount,
      workflowRefreshVersion,
      recordRefreshVersion,
      lastWorkflowEvent,
      newBookingCount,
      newOrderCount,
      newRequestOrderCount,
      markAllRead,
      markRead,
      refreshAssignedTaskCount,
      clearNewBookingCount,
      clearNewOrderCount,
      clearNewRequestOrderCount,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
